import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Hospital, Pill, FlaskConical, ArrowUpRight, CheckCircle2 } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import pharmacySolutionImg from "../assets/pharmacy-solution.png";
import hospitalManagementImg from "../assets/Hospital-Management-System.png";
import laboratoryManagementImg from "../assets/Laboratory-Management.png";

const SOLUTIONS = [
  {
    icon: Hospital,
    code: "HMS",
    title: "Hospital Management System",
    tagline: "The OS for your hospital.",
    cover: hospitalManagementImg,
    features: [
      "Patient registration, OPD, IPD, OT scheduling",
      "Billing, GST invoicing, insurance & TPA cashless",
      "Pharmacy, radiology, lab — all integrated",
      "Role-based access, audit logs, multi-branch",
      "Doctor consultation dashboard & e-prescriptions",
      "Real-time bed & inventory management",
    ],
  },
  {
    icon: Pill,
    code: "Rx",
    title: "Pharmacy Solution",
    tagline: "Inventory + Compliance + Speed.",
    cover: pharmacySolutionImg,
    features: [
      "Barcode billing & GST-compliant invoices",
      "Batch, expiry, supplier & purchase orders",
      "Multi-branch stock transfers in real time",
      "Prescription queue with doctor app sync",
      "WhatsApp refill reminders to patients",
      "Sales analytics & margin intelligence",
    ],
  },
  {
    icon: FlaskConical,
    code: "Lab",
    title: "Laboratory Management",
    tagline: "From sample to digital report.",
    cover: laboratoryManagementImg,
    features: [
      "Test booking, sample collection, barcoding",
      "PACS / DICOM / lab machine integrations",
      "Digital reports auto-pushed to Health ID",
      "Result entry, validation & doctor sign-off",
      "Home collection routing & technician app",
      "Quality control & turnaround analytics",
    ],
  },
];

export default function Solutions() {
  return (
    <div data-testid="page-solutions">
      <section className="relative bg-[color:var(--paper-2)] overflow-hidden">
        <div className="absolute inset-0 bg-grid-light opacity-40" />
        <div className="relative container-x pt-4 md:pt-6 lg:pt-8 pb-16">
          <SectionHeader
            eyebrow="Solutions"
            title="Built for every facility,"
            titleAccent="from clinic to chain."
            subtitle="Three deeply integrated SaaS modules that share data, billing, patients and intelligence — the way modern healthcare should run."
          />
        </div>
      </section>

      <section className="section-y bg-white">
        <div className="container-x space-y-20">
          {SOLUTIONS.map((s, i) => (
            <motion.div
              key={s.code}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`grid lg:grid-cols-12 gap-10 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}
              data-testid={`solution-${s.code.toLowerCase()}`}
            >
              <div className="lg:col-span-6">
                <div className="relative aspect-[4/3] rounded-3xl overflow-hidden">
                  <img src={s.cover} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/75 to-transparent" />
                  <div className="absolute top-6 left-6 px-3 py-1 rounded-full bg-white/95 text-xs font-bold tracking-wider uppercase text-[color:var(--brand)]">
                    {s.code} Module
                  </div>
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <h3 className="text-3xl md:text-4xl font-bold">{s.title}</h3>
                    <p className="text-slate-200 mt-1">{s.tagline}</p>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-6">
                <div className="w-12 h-12 rounded-xl bg-[color:var(--brand-soft)] grid place-items-center text-[color:var(--brand)] mb-4">
                  <s.icon className="w-6 h-6" />
                </div>
                <div className="eyebrow">{s.code} · Core Module</div>
                <h2 className="mt-2 text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.title}</h2>
                <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                  {s.features.map(f => (
                    <li key={f} className="flex gap-2.5 items-start text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-[color:var(--brand)] mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex gap-3">
                  {/*
                  <Link to="/request-demo" className="btn-primary" data-testid={`solution-${s.code.toLowerCase()}-demo`}>
                    Request Demo <ArrowUpRight className="w-4 h-4" />
                  </Link>
                  */}
                  <Link to="/pricing" className="btn-ghost">See Pricing</Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
